import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import moment from "moment";
import "moment/locale/th";
import SelectStepModal from "../../components/Modal/Contract/SelectStepModal";
import TempEquipmentModal from "../../components/Modal/Contract/TempEquipmentModal";
import api from "../../../utils/axios";
import { getData, getDataWithComp } from "../../../utils/SamuiUtils";

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
};

const SearchableItemSelect = ({ value, options, onChange, groupMap = {} }) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const selected = options.find((o) => o.Item_Id === value) || null;

  const getLabel = (item) => {
    const groupName = groupMap[String(item.Item_Group)] || "";
    return groupName ? `${groupName} - ${item.Item_Name}` : item.Item_Name;
  };

  useEffect(() => {
    const handleClick = (e) => {
      const inContainer = containerRef.current && containerRef.current.contains(e.target);
      const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!inContainer && !inDropdown) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const openDropdown = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setIsOpen(true);
    setSearch("");
  };

  const filtered = search
    ? options.filter((o) => {
        const s = search.toLowerCase();
        const gName = (groupMap[String(o.Item_Group)] || "").toLowerCase();
        return (
          (o.Item_Code || "").toLowerCase().includes(s) ||
          (o.Item_Name || "").toLowerCase().includes(s) ||
          gName.includes(s)
        );
      })
    : options;

  const dropdownMenu = isOpen && ReactDOM.createPortal(
    <div ref={dropdownRef} style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999, backgroundColor: "#fff", border: "1px solid #d1d5db", borderRadius: "6px", maxHeight: "200px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }}>
      <div onClick={() => { onChange(null); setSearch(""); setIsOpen(false); }}
        style={{ padding: "7px 10px", cursor: "pointer", color: "#9ca3af", fontSize: "13px" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
        -- เลือกสินค้า --
      </div>
      {filtered.map((item) => (
        <div key={item.Item_Id} onClick={() => { onChange(item); setSearch(""); setIsOpen(false); }}
          style={{ padding: "7px 10px", cursor: "pointer", fontSize: "13px", backgroundColor: value === item.Item_Id ? "#fef3c7" : "transparent" }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
          onMouseLeave={(e) => (e.currentTarget.style.background = value === item.Item_Id ? "#fef3c7" : "transparent")}>
          {getLabel(item)}
        </div>
      ))}
      {filtered.length === 0 && (
        <div style={{ padding: "7px 10px", color: "#9ca3af", fontSize: "13px" }}>ไม่พบสินค้า</div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div style={{ display: "flex", border: "1px solid #d1d5db", borderRadius: "6px", overflow: "hidden", height: "38px", backgroundColor: "#fff" }}>
        <input
          type="text"
          value={isOpen ? search : (selected ? getLabel(selected) : "")}
          onChange={(e) => { setSearch(e.target.value); if (!isOpen) openDropdown(); }}
          onFocus={openDropdown}
          placeholder={isOpen ? "พิมพ์ชื่อ/รหัสสินค้า..." : "-- เลือกสินค้า --"}
          style={{ flex: 1, border: "none", padding: "0 8px", outline: "none", fontSize: "13px", backgroundColor: "transparent", height: "100%", lineHeight: "38px" }}
        />
        {value && (
          <button type="button" onClick={() => { onChange(null); setSearch(""); setIsOpen(false); }}
            style={{ border: "none", background: "none", padding: "0 5px", color: "#9ca3af", cursor: "pointer", fontSize: "15px" }}>×</button>
        )}
        <button type="button" onClick={() => { if (isOpen) { setIsOpen(false); setSearch(""); } else openDropdown(); }}
          style={{ border: "none", borderLeft: "1px solid #d1d5db", background: "#f9fafb", padding: "0 8px", cursor: "pointer", color: "#6b7280", fontSize: "12px" }}>▾</button>
      </div>
      {dropdownMenu}
    </div>
  );
};

const LEGACY_UNIT_MAP = {
  1: "days",
  2: "weeks",
  3: "months",
  4: "years",
  5: "months",
};

const normalizeServiceName = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");

export default function ContractServiceWorkTable({
  works = [],
  setWorks,
  startDate,
  endDate,
  serviceTime = 1,
  serviceUnit = null,
  contractPeriod = null,
  contractUnit = null,
  contractServices = [], // services ที่เลือกในสัญญา (จาก ContractPage)
  hasSubLocations = false, // มีจุดย่อย → ใช้เฉพาะบริการจากแพ็กเกจที่เลือก (ไม่ fallback ไปทั้งหมด)
  isReadOnly = false,
}) {
  const [intervalMonths, setIntervalMonths] = useState(serviceTime || 1);
  const [openGroup, setOpenGroup] = useState({ 0: true });
  const [focusedQtyKey, setFocusedQtyKey] = useState(null);
  const [localQtyValue, setLocalQtyValue] = useState("");
  const [serviceList, setServiceList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalServiceData, setModalServiceData] = useState(null);
  const currentRoundIndexRef = useRef(null);
  const currentServiceIndexRef = useRef(null);

  const [showEquipModal, setShowEquipModal] = useState(false);
  const equipRoundIdxRef = useRef(null);
  const [timeUnitMap, setTimeUnitMap] = useState({});
  const [itemList, setItemList] = useState([]);
  const [workItemList, setWorkItemList] = useState([]);
  const [tempEquipGroupId, setTempEquipGroupId] = useState(null);
  const [groupMap, setGroupMap] = useState({});


  // อัปเดตจำนวนรอบบริการเมื่อ serviceTime เปลี่ยน
  useEffect(() => {
    if (serviceTime && serviceTime > 0) {
      setIntervalMonths(serviceTime);
    }
  }, [serviceTime]);

  // ดึงข้อมูลบริการและขั้นตอนจาก Pack_Id ที่เลือกในสัญญา
  useEffect(() => {
    const fetchServicesWithSteps = async () => {
      try {
        const compId = localStorage.getItem("company");
        const mapServiceRows = (rows = []) =>
          rows.map((service) => ({
            Service_Id: service.Service_Id,
            Service_Name: service.Service_Name,
            Steps: (service.Steps || []).map((step) => ({
              Step_Id: step.Step_Id,
              Step_Title: step.Step_Title,
              Step_Qty: step.Step_Qty,
              Step_Unit: step.Step_Unit,
              IsDefault: step.IsDefault || 0,
              Item_Name: step.Item_Name || null,
              Item_Code: step.Item_Code || null,
              Item_Id: step.Item_Id || null,
            })),
          }));

        const fetchAllServicesWithSteps = async () => {
          const res = await api.get(`/get-services-with-steps${compId ? `?Comp_Id=${compId}` : ""}`);
          if (res.data.status !== "OK") return [];
          return mapServiceRows(res.data.result || []);
        };

        // ถ้ามี contractServices ให้ดึง services จาก Pack_Id
        if (Array.isArray(contractServices) && contractServices.length > 0) {
          // ดึง Pack_Id ทั้งหมดจาก contractServices (Item_Id = Pack_Id)
          const packIds = contractServices
            .map((s) => s.id)
            .filter((id) => id != null);

          // ดึง services จากแต่ละ Pack_Id
          const allServices = [];
          if (packIds.length > 0) {
            for (const packId of packIds) {
              try {
                const res = await api.get(`/get-package-services-with-steps/${packId}`);
                if (res.data.status === "OK") {
                  allServices.push(...mapServiceRows(res.data.result || []));
                }
              } catch (error) {
                console.error(`Error fetching services for Pack_Id ${packId}:`, error);
              }
            }
          }

          // ลบ services ที่ซ้ำกัน (ถ้ามี Pack_Id หลายตัวที่มี Service_Id เดียวกัน)
          const uniqueServices = Array.from(
            new Map(allServices.map((s) => [s.Service_Id, s])).values()
          );

          // Fallback: ถ้าดึงจากแพ็กเกจไม่เจอ ให้ดึงจาก master ตาม "บริการที่เลือก"
          const selectedNames = new Set(
            (contractServices || [])
              .map((s) => normalizeServiceName(s.name))
              .filter(Boolean)
          );

          if (selectedNames.size > 0) {
            const allMasterServices = await fetchAllServicesWithSteps();
            const selectedMaster = allMasterServices.filter((svc) => {
              const svcName = normalizeServiceName(svc.Service_Name);
              if (!svcName) return false;
              for (const selectedName of selectedNames) {
                if (
                  svcName === selectedName ||
                  svcName.replace(/\s/g, "") === selectedName.replace(/\s/g, "") ||
                  svcName.includes(selectedName) ||
                  selectedName.includes(svcName)
                ) {
                  return true;
                }
              }
              return false;
            });

            const merged = Array.from(
              new Map([...uniqueServices, ...selectedMaster].map((s) => [s.Service_Id, s])).values()
            );
            setServiceList(merged);
          } else {
            // ไม่มีจุดย่อย → ใช้ทุกบริการในแพ็กเกจ | มีจุดย่อย → ใช้เฉพาะบริการจากแพ็กเกจที่เลือก (assign ตามแพ็กเกจ)
            setServiceList(uniqueServices);
          }
        } else {
          // ถ้าไม่มี contractServices
          if (hasSubLocations) {
            // มีจุดย่อยแต่ไม่มีแพ็กเกจในสัญญา → ไม่ fallback ไปดึงทุกบริการ (จะได้เฉพาะแพ็กเกจจริงๆ เช่น แพ็คยุง)
            setServiceList([]);
          } else {
            // ไม่มีจุดย่อย → fallback ดึง services ทั้งหมด (กรณีสร้างสัญญาใหม่ยังไม่เลือกบริการ)
            const services = await fetchAllServicesWithSteps();
            setServiceList(services);
          }
        }
      } catch (error) {
        console.error("Error fetching services with steps:", error);
        setServiceList([]);
      }
    };

    fetchServicesWithSteps();
  }, [contractServices, hasSubLocations]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const [items, groups] = await Promise.all([
          getDataWithComp(
            "Tb_Set_Item",
            "AND Item_Status = 'Y' ORDER BY Item_Short_Name, Item_Name",
            { fields: ["Item_Id", "Item_Code", "Item_Name", "Item_Unit_ST", "Item_Group"] }
          ),
          getDataWithComp(
            "Tb_Set_ItmGroup",
            "AND IsShowInWork = 1 AND Group_Status = 'Y'",
            { fields: ["Group_Id", "Group_Name"] }
          ),
        ]);
        const allItems = Array.isArray(items) ? items : [];
        setItemList(allItems);

        const allowedGroups = Array.isArray(groups) ? groups : [];
        if (allowedGroups.length > 0) {
          const allowedIds = new Set(allowedGroups.map((g) => String(g.Group_Id)));
          setWorkItemList(allItems.filter((i) => allowedIds.has(String(i.Item_Group))));
        } else {
          setWorkItemList(allItems);
        }

        const map = {};
        allowedGroups.forEach((g) => { map[String(g.Group_Id)] = g.Group_Name; });
        setGroupMap(map);

        const tempG = allowedGroups.find((g) =>
          String(g.Group_Name || "").toLowerCase().includes("ชั่วคราว")
        );
        setTempEquipGroupId(tempG ? String(tempG.Group_Id) : null);
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    fetchItems();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const normalizeList = (res) => {
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.data)) return res.data;
      return [];
    };

    const fetchTimeUnits = async () => {
      try {
        const res = await getData(
          "Tb_Set_TimeUnit",
          "UPPER(TimeUnit_Type) = 'CONTRACT' AND UPPER(TimeUnit_Status) = 'Y'"
        );
        const list = normalizeList(res);
        const map = list.reduce((acc, item) => {
          if (item?.TimeUnit_Code && item?.TimeUnit_Moment) {
            acc[Number(item.TimeUnit_Code)] = item.TimeUnit_Moment;
          }
          return acc;
        }, {});
        if (isMounted) setTimeUnitMap(map);
      } catch (error) {
        console.error("Error fetching time units:", error);
      }
    };

    fetchTimeUnits();
    return () => {
      isMounted = false;
    };
  }, []);

  // สร้าง serviceTemplates จาก services ที่ดึงมาจาก Pack_Id
  // serviceList จะมี services ที่ดึงมาจาก Pack_Id ที่เลือกในสัญญาแล้ว
  // สำหรับ "กำหนดค่าเริ่มต้น" ให้แสดงเฉพาะ default steps เท่านั้น
  const serviceTemplates = useMemo(() => {
    // ใช้ services จาก serviceList (ที่ดึงมาจาก Pack_Id แล้ว)
    // กรองเฉพาะ steps ที่เป็น default สำหรับ "กำหนดค่าเริ่มต้น"
    return serviceList.map((s) => ({
      serviceId: s.Service_Id ?? null,
      name: s.Service_Name,
      steps: (s.Steps || [])
        .filter((st) => st.IsDefault === 1) // กรองเฉพาะ default steps สำหรับ "กำหนดค่าเริ่มต้น"
        .map((st) => ({
          id: uuid(),
          title: st.Step_Title,
          qty: st.Step_Qty != null && st.Step_Qty !== "" ? String(st.Step_Qty) : "0.00",
          unit: st.Step_Unit || "",
          remark: "",
          checked: true, // checked all เมื่อกำหนดค่าเริ่มต้น
          refStepId: st.Step_Id,
          itemId: st.Item_Id || null,
          itemName: st.Item_Name || "",
          itemCode: st.Item_Code || "",
          itemGroup: st.Item_Group ?? null,
        })),
    }));
  }, [serviceList]);

  const inputClass =
    "w-full h-[38px] border border-gray-300 rounded-md px-2 py-1 text-[14px] focus:ring-1 focus:ring-[#E13F57] focus:border-[#E13F57] transition text-gray-700 placeholder-gray-400";

  const normalizeQtyInput = (raw) => {
    const cleaned = String(raw ?? "").replace(/,/g, "").trim();
    if (!cleaned) return "0.00";
    if (!/^\d*\.?\d*$/.test(cleaned)) return null;
    const [intPartRaw = "0", decPartRaw = ""] = cleaned.split(".");
    const intPart = intPartRaw.replace(/^0+(?=\d)/, "") || "0";
    const decPart = decPartRaw.slice(0, 2).padEnd(2, "0");
    return `${intPart}.${decPart}`;
  };

  const formatQtyDisplay = (raw) => {
    const normalized = normalizeQtyInput(raw);
    if (normalized == null) return "0.00";
    const [intPart, decPart = "00"] = normalized.split(".");
    return `${Number(intPart || 0).toLocaleString("en-US")}.${decPart}`;
  };

  const parseAD = (d) => {
    if (!d) return null;
    const m = moment(d, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
    if (!m.isValid()) return null;
    if (m.year() > 2400) return m.subtract(543, "year");
    return m;
  };

  const beMonthYear = (m) => {
    if (!m || !m.isValid()) return "-";
    return `${m.format("MMMM")} ${m.year() + 543}`;
  };

  // คำนวณ endDate จากระยะสัญญา (Contract_Period และ Contract_Unit)
  // ตัวอย่าง: สัญญา 3 เดือน เริ่มธันวาคม -> สิ้นสุดกุมภาพันธ์ (3 รอบ: ธันวาคม, มกราคม, กุมภาพันธ์)
  const calculatedEndDate = useMemo(() => {
    if (!startDate || !contractPeriod || !contractUnit) {
      return endDate; // ใช้ endDate ที่ส่งมา ถ้าไม่มีข้อมูลระยะสัญญา
    }

    const start = parseAD(startDate);
    if (!start || !start.isValid()) {
      return endDate;
    }

    const period = Number(contractPeriod);
    if (!period || period <= 0) {
      return endDate;
    }

    const unitCode = Number(contractUnit);
    const momentUnit = timeUnitMap[unitCode] || LEGACY_UNIT_MAP[unitCode];
    if (!momentUnit) {
      return endDate;
    }

    let end = start.clone();
    if (momentUnit === "months") {
      // สำหรับเดือน: ถ้าสัญญา 3 เดือน เริ่มธันวาคม -> สิ้นสุดกุมภาพันธ์ (เดือนที่ 3)
      // ใช้ startOf("month") แล้วบวก period-1 เดือน เพื่อให้ได้เดือนสุดท้ายที่ควรมีบริการ
      end = start.clone().startOf("month").add(period - 1, "months").endOf("month");
    } else if (momentUnit === "years") {
      // 1 ปี = 12 เดือน: ใช้ period*12 - 1 เดือน เพื่อให้นับเดือนสุดท้ายถูกต้อง
      end = start.clone().startOf("month").add(period * 12 - 1, "months").endOf("month");
    } else {
      end.add(period, momentUnit);
    }

    // คืนค่าเป็น string format YYYY-MM-DD
    return end.format("YYYY-MM-DD");
  }, [startDate, contractPeriod, contractUnit, endDate, timeUnitMap]);

  const SERVICE_UNIT_TYPES = {
    ROUND: 1,          // รอบ (จำนวนรอบคงที่)
    MONTHS_PER_TIME: 2, // เดือน/ครั้ง (ทุก N เดือน)
    YEARS_PER_TIME: 3,  // ปี/ครั้ง (ทุก N ปี)
  };

  const calculateRoundsFromQuote = useCallback(() => {
    const interval = Number(serviceTime) || 0;
    if (!interval) return 0;

    const unitType = Number(serviceUnit);
    if (!unitType || unitType === SERVICE_UNIT_TYPES.ROUND) {
      return interval; // จำนวนรอบคงที่
    }

    const start = parseAD(startDate);
    const end = parseAD(calculatedEndDate);
    if (!start || !end) {
      return interval;
    }

    const startMonth = start.clone().startOf("month");
    const endMonth = end.clone().startOf("month");
    const monthCount = endMonth.diff(startMonth, "months") + 1;
    if (monthCount <= 0) {
      return interval;
    }

    const serviceMomentUnit = timeUnitMap[unitType] || LEGACY_UNIT_MAP[unitType];
    const isMonthsPerTime =
      unitType === SERVICE_UNIT_TYPES.MONTHS_PER_TIME || serviceMomentUnit === "months";
    const isYearsPerTime =
      unitType === SERVICE_UNIT_TYPES.YEARS_PER_TIME || serviceMomentUnit === "years";

    if (isMonthsPerTime) {
      // ทุก N เดือน → จำนวนรอบ = floor(monthCount / N)
      return Math.max(1, Math.floor(monthCount / interval));
    }

    if (isYearsPerTime) {
      // ทุก N ปี → จำนวนรอบ = floor(yearCount / N)
      const yearCount = Math.floor(monthCount / 12);
      return Math.max(1, Math.floor(yearCount / interval));
    }

    return interval;
  }, [serviceTime, serviceUnit, startDate, calculatedEndDate, timeUnitMap]);

  useEffect(() => {
    const nextRounds = calculateRoundsFromQuote();
    if (nextRounds > 0) {
      setIntervalMonths(nextRounds);
    }
  }, [calculateRoundsFromQuote]);

  const buildRoundsInterval = useCallback((s, e, roundCount, templates) => {
    const start = parseAD(s);
    const end = parseAD(e);
    if (!start || !end) return [];

    const rounds = [];
    const totalRounds = Number(roundCount);
    if (!totalRounds || totalRounds <= 0) return rounds;

    if (totalRounds === 1) {
      rounds.push({
        id: uuid(),
        no: 1,
        date: start.format("YYYY-MM-DD"),
        services: (templates || []).map((srv) => ({
          id: uuid(),
          serviceRefId: srv.serviceId ?? null,
          name: srv.name,
          steps: (srv.steps || []).map((st) => ({
            ...st,
            id: uuid(),
            checked: !!st.checked,
          })),
        })),
      });
      return rounds;
    }

    const totalDays = end.diff(start, "days");
    const stepDays = totalDays / (totalRounds - 1);

    for (let i = 0; i < totalRounds; i += 1) {
      const roundDate =
        i === totalRounds - 1
          ? end.clone()
          : start.clone().add(stepDays * i, "days");

      rounds.push({
        id: uuid(),
        no: i + 1,
        date: roundDate.format("YYYY-MM-DD"),
        services: (templates || []).map((srv) => ({
          id: uuid(),
          serviceRefId: srv.serviceId ?? null,
          name: srv.name,
          steps: (srv.steps || []).map((st) => ({
            ...st,
            id: uuid(),
            checked: !!st.checked,
          })),
        })),
      });
    }

    return rounds;
  }, []);

  const formatBE = (d) => {
    const m = parseAD(d);
    if (!m) return "";
    return m.add(543, "year").format("DD/MM/YYYY");
  };



  const normalizeWorks = useCallback((arr) => {
    let changed = false;
    const next = (arr || []).map((r, rIdx) => {
      const rId = r?.id || uuid();
      if (!r?.id) changed = true;
      const services = (r?.services || []).map((srv) => {
        const sId = srv?.id || uuid();
        if (!srv?.id) changed = true;
        const steps = (srv?.steps || []).map((st) => {
          const stId = st?.id || uuid();
          if (!st?.id) changed = true;
          return {
          ...st,
          id: stId,
          checked: !!st.checked,
          itemId: st.itemId ?? st.Item_Id ?? null,
          itemName: st.itemName ?? st.Item_Name ?? "",
          itemCode: st.itemCode ?? st.Item_Code ?? "",
          itemGroup: st.itemGroup ?? st.Item_Group ?? null,
        };
        });
        return { ...srv, id: sId, steps };
      });
      return { ...r, id: rId, no: r?.no ?? rIdx + 1, services };
    });
    return { next, changed };
  }, []);

  useEffect(() => {
    if (!Array.isArray(works) || works.length === 0) return;
    const { next, changed } = normalizeWorks(works);
    if (changed) setWorks(next);
  }, [works, normalizeWorks, setWorks]);

  const generateWorks = () => {
    const newWorks = buildRoundsInterval(
      startDate,
      calculatedEndDate,
      intervalMonths,
      serviceTemplates
    );
    setWorks(newWorks);
    const open = {};
    newWorks.forEach((_, idx) => (open[idx] = idx === 0));
    setOpenGroup(open);
  };

  useEffect(() => {
    if (!Array.isArray(works) || works.length === 0) return;
    setOpenGroup((prev) => {
      const open = {};
      works.forEach((_, idx) => (open[idx] = prev[idx] ?? idx === 0));
      return open;
    });
  }, [works]);

  const toggleGroup = (roundIndex) => {
    setOpenGroup((p) => ({ ...p, [roundIndex]: !p[roundIndex] }));
  };

  const roundChange = (roundIndex, field, value) => {
    setWorks((prev) =>
      prev.map((r, i) => (i === roundIndex ? { ...r, [field]: value } : r))
    );
  };

  const stepChange = (roundIndex, serviceIndex, stepIndex, field, value) => {
    setWorks((prev) =>
      prev.map((r, ri) => {
        if (ri !== roundIndex) return r;
        const services = (r.services || []).map((srv, si) => {
          if (si !== serviceIndex) return srv;
          const steps = (srv.steps || []).map((st, sti) =>
            sti === stepIndex ? { ...st, [field]: value } : st
          );
          return { ...srv, steps };
        });
        return { ...r, services };
      })
    );
  };

  const stepItemChange = (roundIndex, serviceIndex, stepIndex, item) => {
    setWorks((prev) =>
      prev.map((r, ri) => {
        if (ri !== roundIndex) return r;
        const services = (r.services || []).map((srv, si) => {
          if (si !== serviceIndex) return srv;
          const steps = (srv.steps || []).map((st, sti) =>
            sti === stepIndex
              ? {
                  ...st,
                  itemId: item ? item.Item_Id : null,
                  itemName: item ? item.Item_Name || "" : "",
                  itemCode: item ? item.Item_Code || "" : "",
                  itemGroup: item ? item.Item_Group ?? null : null,
                  unit: item ? (item.Item_Unit_ST || "") : "",
                }
              : st
          );
          return { ...srv, steps };
        });
        return { ...r, services };
      })
    );
  };

  const addBlankStep = (roundIndex, serviceIndex) => {
    setWorks((prev) =>
      prev.map((r, ri) => {
        if (ri !== roundIndex) return r;
        const services = (r.services || []).map((srv, si) => {
          if (si !== serviceIndex) return srv;
          return {
            ...srv,
            steps: [
              ...(srv.steps || []),
              { id: uuid(), title: "", qty: "0.00", unit: "", remark: "", checked: true, refStepId: null, itemId: null, itemName: "", itemCode: "" },
            ],
          };
        });
        return { ...r, services };
      })
    );
  };

  const removeStep = (roundIndex, serviceIndex, stepId) => {
    setWorks((prev) =>
      prev.map((r, ri) => {
        if (ri !== roundIndex) return r;
        const services = (r.services || []).map((srv, si) => {
          if (si !== serviceIndex) return srv;
          const steps = (srv.steps || []).filter((s) => s.id !== stepId);
          return { ...srv, steps };
        });
        return { ...r, services };
      })
    );
  };

  const openAddStepModal = (roundIndex, serviceIndex, serviceName, serviceRefId = null) => {
    const byId = serviceRefId != null
      ? serviceList.find((s) => Number(s.Service_Id) === Number(serviceRefId))
      : null;
    const targetName = normalizeServiceName(serviceName);
    const byName = serviceList.find((s) => {
      const masterName = normalizeServiceName(s.Service_Name);
      if (!masterName || !targetName) return false;
      return (
        masterName === targetName ||
        masterName.replace(/\s/g, "") === targetName.replace(/\s/g, "") ||
        masterName.includes(targetName) ||
        targetName.includes(masterName)
      );
    });
    const srv = byId || byName;
    if (!srv) return;
    
    // หา steps ที่ถูกเลือกอยู่แล้วใน service นี้
    const currentRound = works[roundIndex];
    const currentService = currentRound?.services?.[serviceIndex];
    const existingStepIds = new Set(
      (currentService?.steps || []).map((st) => st.refStepId).filter(Boolean)
    );
    
    // เพิ่มข้อมูล steps ที่ถูกเลือกอยู่แล้ว
    const serviceDataWithSelected = {
      ...srv,
      selectedStepIds: existingStepIds,
    };
    
    setModalServiceData(serviceDataWithSelected);
    currentRoundIndexRef.current = roundIndex;
    currentServiceIndexRef.current = serviceIndex;
    setShowModal(true);
  };

  const addStepsFromModal = (stepList) => {
    if (!stepList || stepList.length === 0) return;
    
    // ใช้ค่าล่าสุดจาก ref
    const roundIdx = currentRoundIndexRef.current;
    const serviceIdx = currentServiceIndexRef.current;
    
    if (roundIdx === null || serviceIdx === null || roundIdx === undefined || serviceIdx === undefined) {
      console.warn("Missing roundIndex or serviceIndex", { roundIdx, serviceIdx });
      return;
    }

    setWorks((prev) => {
      return prev.map((r, ri) => {
        if (ri !== roundIdx) return r;
        const services = (r.services || []).map((srv, si) => {
          if (si !== serviceIdx) return srv;
          const existRef = new Set((srv.steps || []).map((x) => x.refStepId));
          const added = stepList
            .filter((st) => !existRef.has(st.Step_Id))
            .map((st) => ({
              id: uuid(),
              title: st.Step_Title,
              qty: st.Step_Qty != null && st.Step_Qty !== "" ? String(st.Step_Qty) : "0.00",
              unit: st.Step_Unit || "",
              remark: "",
              checked: true,
              refStepId: st.Step_Id,
              itemId: st.Item_Id || null,
              itemName: st.Item_Name || "",
              itemCode: st.Item_Code || "",
              itemGroup: st.Item_Group ?? null,
            }));
          return { ...srv, steps: [...(srv.steps || []), ...added] };
        });
        return { ...r, services };
      });
    });
  };

  const openEquipModal = (rIdx) => {
    equipRoundIdxRef.current = rIdx;
    setShowEquipModal(true);
  };

  const saveEquipItems = (itemList) => {
    const rIdx = equipRoundIdxRef.current;
    if (rIdx === null) return;
    setWorks((prev) =>
      prev.map((r, ri) => {
        if (ri !== rIdx) return r;
        const existingIdx = (r.services || []).findIndex((s) => s.serviceType === "TEMP_EQUIPMENT");
        // ถ้าไม่มีรายการ ให้ลบ service ทิ้ง
        if (itemList.length === 0) {
          if (existingIdx >= 0) {
            return { ...r, services: r.services.filter((_, i) => i !== existingIdx) };
          }
          return r;
        }
        const steps = itemList.map((it) => ({
          id: it.id || uuid(),
          title: it.name,
          qty: it.qty,
          unit: it.unit,
          remark: it.remark,
          checked: false,
          refStepId: null,
        }));
        if (existingIdx >= 0) {
          const services = r.services.map((srv, si) =>
            si === existingIdx ? { ...srv, steps } : srv
          );
          return { ...r, services };
        }
        // สร้างใหม่
        const newSrv = {
          id: uuid(),
          name: "อุปกรณ์ชั่วคราว",
          serviceType: "TEMP_EQUIPMENT",
          serviceStatus: 0,
          steps,
        };
        return { ...r, services: [...(r.services || []), newSrv] };
      })
    );
  };

  // คัดลอกข้อมูลทั้ง round (ทุก service) จาก round ปัจจุบันไปยัง round ถัดไปทั้งหมด
  const copyRoundToNextRounds = (roundIndex) => {
    if (roundIndex >= works.length - 1) return; // ไม่มี round ถัดไป

    setWorks((prev) => {
      // หา round ที่จะ copy
      const sourceRound = prev[roundIndex];
      
      if (!sourceRound || !sourceRound.services || sourceRound.services.length === 0) {
        return prev;
      }

      // Deep copy ทุก service และ steps ใน round นี้
      const copiedServices = sourceRound.services.map((service) => ({
        ...service,
        id: uuid(), // สร้าง ID ใหม่
        steps: (service.steps || []).map((step) => ({
          ...step,
          id: uuid(), // สร้าง ID ใหม่สำหรับแต่ละ step
        })),
      }));

      // Copy ไปยัง round ถัดไปทั้งหมด
      return prev.map((round, ri) => {
        if (ri <= roundIndex) return round; // ข้าม round ปัจจุบันและก่อนหน้า
        
        // สำหรับแต่ละ service ที่ copy มา
        let newServices = [...(round.services || [])];
        
        copiedServices.forEach((copiedService) => {
          // หา service ที่มีชื่อเดียวกันใน round นี้
          const targetServiceIndex = newServices.findIndex(
            (srv) => srv.name === copiedService.name
          );

          if (targetServiceIndex >= 0) {
            // ถ้ามี service ที่ชื่อเดียวกัน ให้ replace
            newServices[targetServiceIndex] = {
              ...copiedService,
              id: uuid(), // สร้าง ID ใหม่สำหรับแต่ละ round
              steps: copiedService.steps.map((step) => ({
                ...step,
                id: uuid(), // สร้าง ID ใหม่สำหรับแต่ละ step ในแต่ละ round
              })),
            };
          } else {
            // ถ้าไม่มี service ที่ชื่อเดียวกัน ให้เพิ่มใหม่
            newServices.push({
              ...copiedService,
              id: uuid(), // สร้าง ID ใหม่สำหรับแต่ละ round
              steps: copiedService.steps.map((step) => ({
                ...step,
                id: uuid(), // สร้าง ID ใหม่สำหรับแต่ละ step ในแต่ละ round
              })),
            });
          }
        });

        return { ...round, services: newServices };
      });
    });
  };

  const startM = parseAD(startDate);
  const endM = parseAD(calculatedEndDate);

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mt-6 border border-gray-300 text-[14px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-bold text-gray-700 text-lg flex items-center gap-2">
          <Icon
            icon="mdi:clipboard-list-outline"
            className="w-5 h-5 text-[#E13F57]"
          />
          รายละเอียดจ๊อบงาน
        </h3>

        <div className="flex flex-wrap items-center gap-2">
          <span>จำนวนรอบบริการ</span>
          <input
            type="number"
            min="1"
            value={intervalMonths}
            onChange={(e) =>
              setIntervalMonths(parseInt(e.target.value || "1", 10))
            }
            className="w-20 h-[38px] border border-gray-300 rounded-md text-center px-2 focus:ring-1 focus:ring-[#E13F57]"
          />
          <span>รอบ</span>

          <span className="ml-2">ตั้งแต่</span>
          <div className="h-[38px] px-3 flex items-center rounded-md border border-gray-300 bg-gray-50 text-gray-700">
            {beMonthYear(startM)}
          </div>

          <span>ถึง</span>
          <div className="h-[38px] px-3 flex items-center rounded-md border border-gray-300 bg-gray-50 text-gray-700">
            {beMonthYear(endM)}
          </div>

          <button
            onClick={generateWorks}
            className="flex items-center gap-1 bg-[#E13F57] hover:bg-[#c53048] text-white px-4 h-[38px] rounded-md shadow ml-1"
          >
            <Icon icon="mdi:refresh" className="w-5 h-5" />
            กำหนดค่าเริ่มต้น
          </button>
        </div>
      </div>

      {(!works || works.length === 0) && (
        <div className="text-center py-6 text-gray-400 border border-dashed rounded-lg">
          ยังไม่มีข้อมูลการทำบริการ
        </div>
      )}

      {(works || []).map((round, rIdx) => (
        <div
          key={round.id}
          className="border border-gray-300 rounded-xl mb-4 overflow-hidden"
        >
          <div className="flex items-center justify-between bg-gray-50 px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleGroup(rIdx)}
                className="w-7 h-7 flex items-center justify-center rounded-md bg-white border border-gray-300"
              >
                <Icon
                  icon={openGroup[rIdx] ? "mdi:chevron-up" : "mdi:chevron-down"}
                  className="w-5 h-5 text-gray-700"
                />
              </button>

              <div className="font-bold text-gray-700">ครั้งที่ {round.no}</div>
            </div>

            <div className="flex items-center gap-2">
              {!isReadOnly && rIdx < works.length - 1 && (
                <button
                  type="button"
                  onClick={() => copyRoundToNextRounds(rIdx)}
                  className="flex items-center gap-1 bg-[#16a34a] hover:bg-[#15803d] text-white px-3 h-[30px] rounded-md shadow text-[12px]"
                  title="คัดลอกข้อมูลบริการทั้งหมดจากครั้งนี้ไปยังครั้งถัดไปทั้งหมด"
                >
                  <Icon icon="mdi:content-copy" className="w-4 h-4" />
                  นำไปใช้กับบริการครั้งต่อไป
                </button>
              )}
              {/* <button
                type="button"
                onClick={() => openEquipModal(rIdx)}
                disabled={isReadOnly}
                className={`flex items-center gap-1 px-3 h-[30px] rounded-md shadow text-[12px] ${
                  isReadOnly
                    ? "bg-gray-300 cursor-not-allowed text-white"
                    : "bg-[#D97706] hover:bg-[#b45309] text-white"
                }`}
              >
                <Icon icon="mdi:toolbox-outline" className="w-4 h-4" />
                อุปกรณ์ชั่วคราว
                {(() => {
                  const cnt = (round.services || []).find((s) => s.serviceType === "TEMP_EQUIPMENT")?.steps?.length || 0;
                  return cnt > 0 ? (
                    <span className="bg-white text-[#D97706] rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold ml-0.5">
                      {cnt}
                    </span>
                  ) : null;
                })()}
              </button> */}

              <div className="flex items-center gap-2">
                {(() => {
                  // หาเดือนและปีของรอบก่อนหน้าเพื่อจำกัดการเลือก (ถ้าเป็นรอบถัดไป)
                  const prevRound = rIdx > 0 ? works[rIdx - 1] : null;
                  const prevDate = prevRound ? parseAD(prevRound.date) : null;
                  
                  // คำนวณเดือน/ปีต่ำสุดที่เลือกได้ (เดือนถัดไปของรอบก่อนหน้า)
                  let minYearBE = null;
                  let minMonthNum = null;
                  
                  if (prevDate && rIdx > 0) {
                    // ใช้เดือนถัดไปของรอบก่อนหน้าเป็นค่าต่ำสุด
                    const nextMonth = prevDate.clone().add(1, "month");
                    minYearBE = nextMonth.year() + 543;
                    minMonthNum = nextMonth.month() + 1; // moment.month() is 0-based
                  }
                  
                  const currentDate = parseAD(round.date);
                  const currentYearBE = currentDate ? currentDate.year() + 543 : null;
                  const currentMonthNum = currentDate ? currentDate.month() + 1 : null;

                  return (
                    <>
                      {/* เลือกเดือน */}
                      <select
                        value={currentMonthNum ? String(currentMonthNum).padStart(2, "0") : ""}
                        onChange={(e) => {
                          const monthNum = parseInt(e.target.value);
                          if (!monthNum) return;
                          
                          const m = parseAD(round.date) || moment();
                          let newM = m.month(monthNum - 1);
                          
                          // ตรวจสอบว่าต้องอัปเดตปีหรือไม่ (ถ้าเดือนที่เลือกน้อยกว่าเดือนต่ำสุด)
                          if (rIdx > 0 && prevDate && minYearBE && minMonthNum) {
                            const newYearBE = newM.year() + 543;
                            // ถ้าปีน้อยกว่าหรือ (ปีเท่ากันแต่เดือนน้อยกว่า) ให้ใช้ปีและเดือนต่ำสุด
                            if (newYearBE < minYearBE || (newYearBE === minYearBE && monthNum < minMonthNum)) {
                              newM = moment().year(minYearBE - 543).month(minMonthNum - 1);
                            }
                          }
                          
                          // ตั้งเป็นวันแรกของเดือน
                          const newDate = newM.startOf("month").format("YYYY-MM-DD");
                          roundChange(rIdx, "date", newDate);
                        }}
                        disabled={isReadOnly}
                        className={`${inputClass} w-[140px] text-center`}
                      >
                        <option value="">-- เลือกเดือน --</option>
                        {(() => {
                          moment.locale("th");
                          return moment.months().map((month, idx) => {
                            const monthNum = idx + 1;
                            // ถ้าเป็นรอบถัดไป ให้กรองเดือนที่น้อยกว่าเดือนต่ำสุด
                            if (rIdx > 0 && prevDate && minYearBE && minMonthNum && currentYearBE) {
                              // ถ้าปีน้อยกว่าหรือ (ปีเท่ากันแต่เดือนน้อยกว่า) ให้ซ่อน
                              if (currentYearBE < minYearBE || (currentYearBE === minYearBE && monthNum < minMonthNum)) {
                                return null;
                              }
                            }
                            return (
                              <option key={idx} value={String(monthNum).padStart(2, "0")}>
                                {month}
                              </option>
                            );
                          }).filter(Boolean);
                        })()}
                      </select>

                      {/* เลือกปี (พ.ศ.) */}
                      <select
                        value={currentYearBE ? String(currentYearBE) : ""}
                        onChange={(e) => {
                          const yearBE = parseInt(e.target.value);
                          if (!yearBE) return;
                          const yearAD = yearBE - 543;
                          
                          const m = parseAD(round.date) || moment();
                          let newM = m.year(yearAD);
                          
                          // ตรวจสอบว่าต้องอัปเดตเดือนหรือไม่ (ถ้าปีที่เลือกน้อยกว่าปีต่ำสุด)
                          if (rIdx > 0 && prevDate && minYearBE && minMonthNum) {
                            if (yearBE < minYearBE) {
                              // ถ้าปีน้อยกว่าครั้งก่อนหน้า ให้ใช้ปีและเดือนต่ำสุด
                              newM = moment().year(minYearBE - 543).month(minMonthNum - 1);
                            } else if (yearBE === minYearBE && currentMonthNum && currentMonthNum < minMonthNum) {
                              // ถ้าปีเท่ากันแต่เดือนน้อยกว่า ให้ใช้เดือนต่ำสุด
                              newM = moment().year(yearAD).month(minMonthNum - 1);
                            } else {
                              // เก็บเดือนเดิม
                              const currentM = parseAD(round.date);
                              if (currentM) {
                                newM = moment().year(yearAD).month(currentM.month());
                              }
                            }
                          } else {
                            // เก็บเดือนเดิม
                            const currentM = parseAD(round.date);
                            if (currentM) {
                              newM = moment().year(yearAD).month(currentM.month());
                            }
                          }
                          
                          // ตั้งเป็นวันแรกของเดือน
                          const newDate = newM.startOf("month").format("YYYY-MM-DD");
                          roundChange(rIdx, "date", newDate);
                        }}
                        disabled={isReadOnly}
                        className={`${inputClass} w-[100px] text-center`}
                      >
                        <option value="">-- เลือกปี --</option>
                        {Array.from({ length: 10 }, (_, i) => {
                          const year = new Date().getFullYear() + 543 + i - 2; // 2 ปีก่อน ถึง 7 ปีข้างหน้า
                          // ถ้าเป็นรอบถัดไป ให้กรองปีที่น้อยกว่าปีต่ำสุด
                          if (rIdx > 0 && prevDate && minYearBE && year < minYearBE) {
                            return null;
                          }
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        }).filter(Boolean)}
                      </select>
                    </>
                  );
                })()}
              </div>
            </div>


          </div>

          {openGroup[rIdx] && (
            <div className="p-3 bg-white border-t">
              {(round.services || []).filter((s) => s.serviceType !== "TEMP_EQUIPMENT").map((srv, sIdx) => {
                const actualSrvIdx = (round.services || []).findIndex((s) => s.id === srv.id);
                return (
                <div key={srv.id} className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="font-bold text-[#111827] text-[15px]">
                        {sIdx + 1}. {srv.name}
                      </div>
                      <div className="bg-[#FFE5EA] text-[#E13F57] px-3 py-1 rounded-full text-[12px] font-bold">
                        ขั้นตอนการบริการ
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openAddStepModal(rIdx, actualSrvIdx, srv.name, srv.serviceRefId)}
                        disabled={isReadOnly}
                        className={`flex items-center gap-1 px-3 h-[30px] rounded-full shadow text-[12px] ${
                          isReadOnly
                            ? "bg-gray-400 cursor-not-allowed text-white"
                            : "bg-[#1D4ED8] hover:bg-[#163fb2] text-white"
                        }`}
                      >
                        <Icon icon="mdi:plus-circle" className="w-4 h-4" />
                        เพิ่ม ขั้นตอน
                      </button>
                      <button
                        type="button"
                        onClick={() => addBlankStep(rIdx, actualSrvIdx)}
                        disabled={isReadOnly}
                        className={`flex items-center gap-1 px-3 h-[30px] rounded-full shadow text-[12px] ${
                          isReadOnly
                            ? "bg-gray-400 cursor-not-allowed text-white"
                            : "bg-[#059669] hover:bg-[#047857] text-white"
                        }`}
                      >
                        <Icon icon="mdi:plus" className="w-4 h-4" />
                        เพิ่มเอง
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-lg border border-gray-300">
                    <table className="table-fixed w-full border border-gray-300 text-[13px]">
                      <thead>
                        <tr>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[7%] py-2">
                            เลือก
                          </th>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[26%] py-2 px-2">
                            ขั้นตอนบริการ
                          </th>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[16%] py-2 px-2">
                            อุปกรณ์ / สารเคมี
                          </th>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[12%] py-2">
                            ปริมาณ
                          </th>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[10%] py-2">
                            วาง / เก็บคืน
                          </th>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[17%] py-2">
                            หมายเหตุ
                          </th>
                          <th className="bg-[#E13F57] text-white text-center border border-gray-300 w-[6%] py-2"></th>
                        </tr>
                      </thead>

                      <tbody>
                        {(srv.steps || []).length === 0 && (
                          <tr>
                            <td
                              colSpan="6"
                              className="text-center py-4 text-gray-400 border border-gray-300"
                            >
                              ไม่มีขั้นตอน
                            </td>
                          </tr>
                        )}

                        {(srv.steps || []).map((st, stIdx) => {
                          const itemGroup = st.itemGroup != null
                            ? String(st.itemGroup)
                            : st.itemId
                              ? String(workItemList.find((i) => i.Item_Id === st.itemId)?.Item_Group ?? "")
                              : "";
                          const isTempEquip = !!(tempEquipGroupId && itemGroup && itemGroup === tempEquipGroupId);
                          return (
                          <tr key={st.id} className="odd:bg-white even:bg-gray-50">
                            <td className="text-center border border-gray-300 py-2 align-middle">
                              <input
                                type="checkbox"
                                checked={!!st.checked}
                                onChange={(e) =>
                                  stepChange(
                                    rIdx,
                                    actualSrvIdx,
                                    stIdx,
                                    "checked",
                                    e.target.checked
                                  )
                                }
                                className="w-4 h-4 accent-[#E13F57]"
                              />
                            </td>

                            <td className="px-2 border border-gray-300 align-middle">
                              <input
                                type="text"
                                value={st.title || ""}
                                onChange={(e) =>
                                  stepChange(
                                    rIdx,
                                    actualSrvIdx,
                                    stIdx,
                                    "title",
                                    e.target.value
                                  )
                                }
                                className={inputClass}
                              />
                            </td>

                            <td className="px-2 border border-gray-300 align-middle">
                              {isReadOnly ? (
                                <span className="text-gray-700">{st.itemName || "-"}</span>
                              ) : (
                                <SearchableItemSelect
                                  value={st.itemId || null}
                                  options={workItemList}
                                  onChange={(item) => stepItemChange(rIdx, actualSrvIdx, stIdx, item)}
                                  groupMap={groupMap}
                                />
                              )}
                            </td>

                            <td className="px-2 border border-gray-300 align-middle">
                              <div className="flex items-center justify-start gap-1">
                                {(() => {
                                  const qtyKey = `${rIdx}-${actualSrvIdx}-${stIdx}`;
                                  const isEditing = focusedQtyKey === qtyKey;
                                  return (
                                    <input
                                      type="text"
                                      value={isEditing ? localQtyValue : formatQtyDisplay(st.qty)}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (!/^[\d.,]*$/.test(v)) return;
                                        const dotIdx = v.indexOf(".");
                                        if (dotIdx !== -1 && v.length - dotIdx - 1 > 2) return;
                                        setLocalQtyValue(v);
                                      }}
                                      onFocus={(e) => {
                                        setFocusedQtyKey(qtyKey);
                                        setLocalQtyValue(formatQtyDisplay(st.qty));
                                        requestAnimationFrame(() => e.target.select());
                                      }}
                                      onBlur={() => {
                                        const normalized = normalizeQtyInput(localQtyValue) || "0.00";
                                        stepChange(rIdx, actualSrvIdx, stIdx, "qty", normalized);
                                        setFocusedQtyKey(null);
                                        setLocalQtyValue("");
                                      }}
                                      disabled={!st.itemId}
                                      className={`${inputClass} text-right ${!st.itemId ? "bg-gray-100 cursor-not-allowed" : (st.itemId && (!st.qty || Number(st.qty) <= 0) ? "border-red-500 bg-red-50" : "")}`}
                                      style={{ width: 140, flexShrink: 0 }}
                                    />
                                  );
                                })()}
                                {st.itemId && st.unit && (
                                  <span className="text-gray-500 text-[12px] flex-shrink-0">{st.unit}</span>
                                )}
                              </div>
                            </td>

                            <td className="text-center border border-gray-300 align-middle px-1">
                              {isTempEquip && (
                                isReadOnly ? (
                                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${st.isCollect ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                    {st.isCollect ? "เก็บคืน" : "วาง"}
                                  </span>
                                ) : (
                                  <div className="flex items-center justify-center whitespace-nowrap text-[12px] w-full">
                                    <label className="flex items-center cursor-pointer select-none" style={{gap:2, paddingRight:6}}>
                                      <input
                                        type="radio"
                                        name={`collect-${st.id}`}
                                        checked={!st.isCollect}
                                        onChange={() => stepChange(rIdx, actualSrvIdx, stIdx, "isCollect", false)}
                                        className="w-3.5 h-3.5 accent-[#E13F57]"
                                      />
                                      วาง
                                    </label>
                                    <label className="flex items-center cursor-pointer select-none" style={{gap:2}}>
                                      <input
                                        type="radio"
                                        name={`collect-${st.id}`}
                                        checked={!!st.isCollect}
                                        onChange={() => stepChange(rIdx, actualSrvIdx, stIdx, "isCollect", true)}
                                        className="w-3.5 h-3.5 accent-[#E13F57]"
                                      />
                                      เก็บคืน
                                    </label>
                                  </div>
                                )
                              )}
                            </td>

                            <td className="px-2 border border-gray-300 align-middle">
                              <input
                                type="text"
                                value={st.remark || ""}
                                onChange={(e) =>
                                  stepChange(rIdx, actualSrvIdx, stIdx, "remark", e.target.value)
                                }
                                disabled={isReadOnly}
                                className={`${inputClass} ${isReadOnly ? "bg-gray-100 cursor-not-allowed" : ""}`}
                              />
                            </td>

                            <td className="text-center border border-gray-300 align-middle">
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => removeStep(rIdx, actualSrvIdx, st.id)}
                                disabled={isReadOnly}
                              >
                                <Icon icon="mdi:delete" className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                </div>
                );
              })}

              {/* อุปกรณ์ชั่วคราว — แสดงเป็น service แยกต่างหากท้าย round */}
              {/* {(() => {
                const tempSrv = (round.services || []).find((s) => s.serviceType === "TEMP_EQUIPMENT");
                if (!tempSrv || !tempSrv.steps?.length) return null;
                const statusLabel = tempSrv.serviceStatus === 1 ? "รับของแล้ว" : "รอรับของ";
                const statusColor = tempSrv.serviceStatus === 1 ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700";
                return (
                  <div className="mt-2 border border-amber-300 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between bg-[#FEF3C7] px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Icon icon="mdi:toolbox-outline" className="w-4 h-4 text-[#D97706]" />
                        <span className="font-bold text-[#92400E] text-[14px]">อุปกรณ์ชั่วคราว</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${statusColor}`}>
                          {statusLabel}
                        </span>
                      </div>
                    </div>
                    <table className="table-fixed w-full text-[13px]">
                      <thead>
                        <tr>
                          <th className="bg-[#FFFBEB] text-[#92400E] text-left border border-amber-200 px-2 py-1.5 w-[40%]">ชื่ออุปกรณ์ / รายการ</th>
                          <th className="bg-[#FFFBEB] text-[#92400E] text-center border border-amber-200 px-2 py-1.5 w-[12%]">จำนวน</th>
                          <th className="bg-[#FFFBEB] text-[#92400E] text-center border border-amber-200 px-2 py-1.5 w-[15%]">หน่วย</th>
                          <th className="bg-[#FFFBEB] text-[#92400E] text-left border border-amber-200 px-2 py-1.5">หมายเหตุ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tempSrv.steps.map((st) => (
                          <tr key={st.id} className="bg-white hover:bg-amber-50">
                            <td className="border border-amber-200 px-2 py-1">{st.title}</td>
                            <td className="border border-amber-200 px-2 py-1 text-center">{st.qty}</td>
                            <td className="border border-amber-200 px-2 py-1 text-center">{st.unit}</td>
                            <td className="border border-amber-200 px-2 py-1 text-gray-500">{st.remark}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()} */}
            </div>
          )}
        </div>
      ))}

      <SelectStepModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        serviceData={modalServiceData}
        onSave={addStepsFromModal}
      />

      <TempEquipmentModal
        isOpen={showEquipModal}
        onClose={() => setShowEquipModal(false)}
        serviceName={`อุปกรณ์ชั่วคราว ครั้งที่ ${equipRoundIdxRef.current !== null ? (works?.[equipRoundIdxRef.current]?.no ?? "") : ""}`}
        initialItems={
          equipRoundIdxRef.current !== null
            ? ((works?.[equipRoundIdxRef.current]?.services || []).find((s) => s.serviceType === "TEMP_EQUIPMENT")?.steps || []).map((st) => ({
                id: st.id,
                name: st.title,
                qty: st.qty,
                unit: st.unit,
                remark: st.remark,
              }))
            : []
        }
        onSave={saveEquipItems}
      />
    </div>
  );
}
